import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import parseTimeString from "../../../hooks/parseTimeString";
import CustomScroll from "react-custom-scroll";
import { useParams } from "react-router-dom";
import {getVideoList, updateVideoListBySort} from "../../../store/slices/videolist";
import {formatInterval} from "../../../hooks/formatInterval";
import VideoListItem from "./VideoListItem";
import {getColumns} from "../../admin/helper/multiselectColumns";
import {IDashboardConfig, IVideoListData, IObjectKeys, IChannelData} from "../../../constants/interfaces";
import {ensureType} from "../../../hooks/ensureType";
import { getLineWidth, getLineSum } from "../../admin/helper/lineDistribution";
import { dashboardArrowDown, dashboardArrowUp } from "../../admin/icons";


const MAX_LEGEND_ITEMS = 5;

type Props = {
	cellId: number,
	variant: string
}

const VideoListWidget = (props: Props) => {
	const data = useSelector((state: {videolist: {data: IVideoListData[]}}) => state.videolist.data[props.cellId]);
	const isUpdating = useSelector((state: {videolist: {isUpdating: number}}) => state.videolist.isUpdating);
	const [listData, setListData] = useState(data);
	const dispatch = useDispatch();
	const widgetConfig = useSelector(
		(state: {dashboard: {data: IDashboardConfig}}) => ensureType(state.dashboard.data.grid_config.find(cell => cell.cell_id === props.cellId))
	);
	const [sortingField, setSortingField] = useState("");
	const [sortOrder, setSortOrder] = useState("ASC");
	const fields = widgetConfig.fields;
	const refreshRate = parseTimeString(widgetConfig.refresh);
	const numberFields = fields.filter(field => ! ['title','brandName'].includes(field));
	const brandName = fields.find(field => field === 'brandName');
	const brands = widgetConfig.brands.join(',');
	const title = widgetConfig.label;
	const timeWindow = widgetConfig.time_window.replace(/360m/,"6h").replace(/720m/,"12h").replace(/1440m/,"24h")
	const extraInfo = (widgetConfig.time_window === "midnight") ? "Today" : "Last " + timeWindow;
	const apiInterval = formatInterval(widgetConfig.time_window);
	const sections = widgetConfig.sections.join('$$');
	const tags = widgetConfig.tags ? widgetConfig.tags.join('$$') : '';
	const { id } = useParams() as any;

	const isCurrent = useMemo(()=>{
		if (id.toLowerCase().includes("current") || id.toLowerCase().includes("readingtime")) {
			return true;
		} return false;
	},[id])

	// initial first load
	useEffect(() => {
		let sort = "";
		if (sortingField && sortOrder) {
			sort = `${sortingField}.${sortOrder.toLowerCase()}`;
		} else {
			sort = widgetConfig.sort;
		}
		dispatch(getVideoList( brands, apiInterval, sections, tags, sort, props.cellId));
	}, [dispatch, brands, apiInterval, sections, tags, widgetConfig, props.cellId]);

	// update data
	useEffect(() => {
		let sort = "";
		if (sortingField && sortOrder) {
			sort = `${sortingField}.${sortOrder.toLowerCase()}`;
		} else {
			sort = widgetConfig.sort;
		}
		const interval = setInterval(() => {
			dispatch(getVideoList(brands, apiInterval, sections, tags, sort, props.cellId));
		}, refreshRate);
		return () => clearInterval(interval);
	}, [dispatch, refreshRate, brands, apiInterval, sections, tags, widgetConfig, props.cellId]);
	// get relevant data from state array
	useEffect(() => {
		setListData(data);
	}, [data, widgetConfig]);

	const getFieldTitle = (field: string) => {
		return getColumns("video_list").find(item => item.name === field)?.title || "";
	}

	const isLoaded = (listData && listData.length)? true : false;
	if (!listData && !isLoaded) return <div>Loading...</div>;
	if (listData.length === 0) return <div>Loading...</div>;

	// gather all channel types
	const videoChannels = listData.map((p) => p.relativeChannels);
	const allChannels = videoChannels.reduce(
		(p: Array<IChannelData>, n) => p.concat(n.filter((c) => (c.total || 0) > 0)),
		[]
	);
	const channelLabels = Array.from(
		new Set(allChannels.map((c) => c.label)).values()
	);
	const channelTypes = channelLabels.map((label) => {
		const { color } = ensureType(allChannels.find((e) => e.label === label));
		return { label, color };
	}, {});
	const channelMaxes: IObjectKeys = channelLabels.reduce((store: IObjectKeys, label) => {
		store[label] = Math.max(...allChannels.filter(c => c.label === label).map(c => c.total));
		return store;
	}, {});
	const legendChannels = channelLabels.sort((a, b) => channelMaxes[b] - channelMaxes[a]).slice(0, MAX_LEGEND_ITEMS);
	// List item lines
	const totals = listData.map(item => item.total);
	const maxReach = totals.length > 0 ? totals.reduce((a, b) => Math.max(a, b)) : 0;

	const handleSort = (field : string) => {
		let sortingOrder = ""
		if (sortingField === field || widgetConfig.sort.split(".")[0] === field) {
			if (sortOrder === "DESC") {
				setSortOrder("ASC");
				sortingOrder = "ASC";
			} else {
				setSortOrder("DESC");
				sortingOrder = "DESC";
			}
			setSortingField(field);
		} else {
			setSortOrder("DESC");
			sortingOrder = "DESC";
			setSortingField(field);
		}

		const sort = `${field}.${sortingOrder.toLowerCase()}`;
		dispatch(updateVideoListBySort(brands, apiInterval, sections, tags, sort, props.cellId));
	}


	return (
		<div
			className={`list-widget ${
				listData.length > 10 ? "scroll-enabled" : ""
			}`}
		>
			<div className="list-widget__heading">
				<div className="list-widget__title">{title}</div>

				<div className="list-widget__heading-right">
					<div style={{ flex: 1 }} />
					<ul className="legend">
						{channelTypes.filter(t => legendChannels.includes(t.label)).map((t) => (
							<li
								key={t.label}
								className="legend__item"
								style={{ color: t.color }}
							>
								<span>{t.label}</span>
							</li>
						))}
					</ul>
					{/*{variant === 'full' ? setBrand() : null}*/}
				</div>
			</div>
			<span className="list-widget__extra-info">{extraInfo}</span>

			<div className="list-widget__list">
				<div className="list-widget__fields">
					<div className="list-widget__heading-header" />
					<div className={`list-widget__heading-headers dynamic-flex-${
						numberFields.length + Number(props.variant === 'full') + (isNaN(Number(brandName))? 0 : 1)
						}`} >
					{numberFields.map((field, index) => (
						<div
						className={`list-widget-stat-heading ${!isCurrent && "sortable"} ${field === "pubdate" ? "date" : ""} ${sortingField === field ? "sorted" : ""}`}
							key={index}
							onClick={()=>{
								if (!isCurrent) {
									handleSort(field)
								}
							}}
						>
							{
									sortingField === field ? 
									sortOrder === "ASC" ? dashboardArrowUp : dashboardArrowDown : ""
							}
							{" "}
							{getFieldTitle(field)}
						</div>
					))}
					{!!brandName && (
						<div className="list-widget-stat-heading list-widget-stat-heading-brand-name">
							Brand
						</div>
					)}
					</div>
				</div>
				<CustomScroll heightRelativeToParent="calc(100% - 80px)">
					{isUpdating === props.cellId ? <span className="loading-sign">
						<span className="loading-sign__spinner" />
						</span> :  listData.map((data, key) => {
						return (
							<VideoListItem
								key={key}
								data={data}
								lineWidth={getLineWidth(data.total, maxReach)}
								lineSum={getLineSum(data.relativeChannels)}
								numberFields={numberFields}
								showBrand={!!brandName}
								variant={props.variant}
							/>
						);
					})}
				</CustomScroll>
			</div>
		</div>
	);
};

export default VideoListWidget;
